import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ArrowRight, ShoppingBag, Sparkles } from "lucide-react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { api, fmtINR } from "../lib/api";
import { BrandLogo } from "../components/Brand";

export default function Cart() {
    const { items, updateQty, setPoints, removeItem } = useCart();
    const { customer, wallet } = useAuth();
    const [preview, setPreview] = useState(null);
    const navigate = useNavigate();

    const reqBody = useMemo(
        () => ({
            items: items.map((i) => ({
                reward_id: i.reward_id,
                denomination: i.denomination,
                points_to_use: i.points_to_use,
                qty: i.qty,
            })),
        }),
        [items]
    );

    useEffect(() => {
        if (!customer) return;
        if (items.length === 0) {
            setPreview(null);
            return;
        }
        api.post("/api/cart/preview", reqBody).then((r) => setPreview(r.data)).catch(() => {});
    }, [reqBody, customer, items.length]);

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 text-center">
                <ShoppingBag size={48} className="mx-auto text-[#cbd5e1]" />
                <h1 className="font-display text-2xl font-bold mt-4">Your cart is empty</h1>
                <p className="text-[#6b7280] mt-1">Discover gift cards from 180+ brands.</p>
                <Link to="/category/all" className="fx-btn-primary inline-flex mt-5">
                    Browse deals <ArrowRight size={14} />
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
                <h1 className="font-display text-2xl md:text-3xl font-bold">Your cart ({items.length})</h1>
                <div className="space-y-3 mt-5">
                    {items.map((i, idx) => {
                        const linePts = preview?.items?.[idx]?.points_used ?? i.points_to_use;
                        const maxPts = preview?.items?.[idx]?.max_points_usable ?? 0;
                        const cashPaise = preview?.items?.[idx]?.cash_paise ?? i.denomination * i.qty * 100;
                        return (
                            <div key={`${i.reward_id}-${i.denomination}`} className="fx-card p-4 flex gap-3">
                                <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center shrink-0">
                                    <BrandLogo url={i.brand_logo_url} name={i.brand_name} size={56} className="!bg-transparent !p-0" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <Link to={`/voucher/${i.reward_id}`} className="font-semibold text-sm hover:text-[#c9a959]">
                                                {i.brand_name} • ₹{i.denomination}
                                            </Link>
                                            <p className="text-[11px] text-[#6b7280]">{i.reward_name}</p>
                                        </div>
                                        <button onClick={() => removeItem(idx)} className="text-[#9ca3af] hover:text-[#ff4d4d]" aria-label="Remove">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center gap-2 fx-glass rounded-full px-2 py-1">
                                            <button onClick={() => updateQty(idx, i.qty - 1)} className="w-6 h-6 rounded-full bg-[#f3f4f6]">−</button>
                                            <span className="font-semibold w-5 text-center text-sm">{i.qty}</span>
                                            <button onClick={() => updateQty(idx, i.qty + 1)} className="w-6 h-6 rounded-full bg-[#f3f4f6]">+</button>
                                        </div>
                                        <p className="text-sm font-bold ml-auto">
                                            {fmtINR(i.denomination * i.qty * 100)}
                                        </p>
                                    </div>

                                    {customer && maxPts > 0 && (
                                        <div className="mt-3">
                                            <div className="flex items-center justify-between text-[11px]">
                                                <span className="text-[#6b7280]">Use points</span>
                                                <span className="text-[#c9a959] font-semibold">
                                                    {linePts.toLocaleString("en-IN")} pts • saves {fmtINR(linePts * 100 / (i.points_per_inr || 4))}
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                min={0}
                                                max={Math.min(maxPts, wallet?.points_balance || 0)}
                                                value={linePts}
                                                onChange={(e) => setPoints(idx, e.target.value)}
                                                className="w-full accent-[#0b1437]"
                                                data-testid={`cart-points-slider-${idx}`}
                                            />
                                            <div className="text-[10px] text-[#9ca3af] text-right">
                                                Pay {fmtINR(cashPaise)} via Razorpay
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Summary */}
            <aside className="md:sticky md:top-24 self-start space-y-3">
                <div className="fx-card p-5">
                    <h3 className="font-display text-lg font-semibold mb-3">Order summary</h3>
                    {!customer && (
                        <div className="rounded-xl bg-[#c9a959]/10 border border-[#c9a959]/30 p-3 text-xs mb-4">
                            <p className="text-[#c9a959] font-semibold">Sign in to use your points</p>
                            <Link to="/login?next=/cart" className="underline text-[#1f2937]">Sign in</Link>
                        </div>
                    )}
                    <Row k="Subtotal" v={fmtINR(items.reduce((s, i) => s + i.qty * i.denomination * 100, 0))} />
                    {preview && (
                        <>
                            <Row k="Points applied" v={`− ${fmtINR(preview.total_points_used * 100 / 4)}`} pos />
                            <div className="border-t border-[rgba(11,20,55,0.10)] my-3" />
                            <Row k="To pay" v={fmtINR(preview.total_cash_paise)} strong />
                            <Row
                                k="Points used"
                                v={`${preview.total_points_used.toLocaleString("en-IN")} pts`}
                                sub
                            />
                        </>
                    )}
                    <button
                        data-testid="proceed-checkout-btn"
                        onClick={() => (customer ? navigate("/checkout") : navigate("/login?next=/cart"))}
                        className="fx-btn-primary w-full mt-4"
                        disabled={items.length === 0}
                    >
                        Proceed to checkout <ArrowRight size={14} />
                    </button>
                </div>
                <div className="fx-card p-4 flex items-start gap-2 text-xs text-[#6b7280]">
                    <Sparkles size={14} className="text-[#c9a959] mt-0.5 shrink-0" />
                    <p>Use points + cash on every order. 4 points = ₹1. Earn more by scanning bills.</p>
                </div>
            </aside>
        </div>
    );
}

function Row({ k, v, pos, strong, sub }) {
    return (
        <div className={`flex justify-between text-sm ${sub ? "text-[#6b7280] text-xs" : ""}`}>
            <span className="text-[#4b5563]">{k}</span>
            <span className={`${pos ? "text-[#c9a959]" : ""} ${strong ? "font-bold text-[#0b1437] text-base" : ""}`}>{v}</span>
        </div>
    );
}
